// src/components/products/forms/CreateProductForm.tsx
import React from "react";

import type {
  CreateProductPayload,
  TaxMethod,
  ProductTaxRulePayload,
  ProductEmailPayload,
} from "@/api/features/products/types";

import {
  MarkupsEditor,
  makeDefaultMarkupRows,
  markupRowsToPayload,
  markupRowError,
  type MarkupRow,
} from "./MarkupsEditor";

type CreateProductFormProps = {
  onSubmit: (payload: CreateProductPayload) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  apiError?: string | null;
};

type TaxRuleState = {
  id?: number;
  taxName: string;
  taxRate: string;
  taxMethod: TaxMethod;
};

type EmailState = {
  id?: number;
  email: string;
  label: string;
};

const defaultTaxRule: TaxRuleState = {
  taxName: "",
  taxRate: "",
  taxMethod: "PERCENT",
};

const defaultEmail: EmailState = {
  email: "",
  label: "",
};

export const CreateProductForm: React.FC<CreateProductFormProps> = ({
  onSubmit,
  onCancel,
  isSubmitting = false,
  apiError,
}) => {
  const [name, setName] = React.useState("");

  // Markups (scoped: customerType x travelScope)
  const [markups, setMarkups] = React.useState<MarkupRow[]>(
    makeDefaultMarkupRows()
  );

  // Tax rules & emails
  const [taxRules, setTaxRules] = React.useState<TaxRuleState[]>([]);
  const [emails, setEmails] = React.useState<EmailState[]>([]);

  const handleAddTaxRule = () => {
    setTaxRules((prev) => [...prev, { ...defaultTaxRule }]);
  };

  const handleUpdateTaxRule = (
    index: number,
    patch: Partial<TaxRuleState>
  ) => {
    setTaxRules((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, ...patch } : item
      )
    );
  };

  const handleRemoveTaxRule = (index: number) => {
    setTaxRules((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddEmail = () => {
    setEmails((prev) => [...prev, { ...defaultEmail }]);
  };

  const handleUpdateEmail = (
    index: number,
    patch: Partial<EmailState>
  ) => {
    setEmails((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, ...patch } : item
      )
    );
  };

  const handleRemoveEmail = (index: number) => {
    setEmails((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = name.trim();
    if (!trimmedName) return;

    // Invalid markup rates are shown inline; never submit them.
    if (markups.some((row) => markupRowError(row))) return;

    const payload: CreateProductPayload = {
      name: trimmedName,

      // Scoped markups (authoritative on the backend)
      markups: markupRowsToPayload(markups),

      taxRules: taxRules
        .map<ProductTaxRulePayload | null>((t) => {
          const taxName = t.taxName.trim();
          const taxRate = t.taxRate.trim();
          if (!taxName || !taxRate) return null;

          return {
            taxName,
            taxRate,
            taxMethod: t.taxMethod,
          };
        })
        .filter(Boolean) as ProductTaxRulePayload[],

      emails: emails
        .map<ProductEmailPayload | null>((e) => {
          const email = e.email.trim();
          const label = e.label.trim();
          if (!email) return null;
          return {
            email,
            label: label || null,
          };
        })
        .filter(Boolean) as ProductEmailPayload[],
    };

    onSubmit(payload);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6 text-sm text-gray-900"
    >
      {/* API error */}
      {apiError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {apiError}
        </div>
      )}

      {/* Core fields */}
      <section className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">
            Product Name <span className="text-red-500">*</span>
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Tours, Flights, Hotels"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
          />
          <p className="mt-1 text-xs text-gray-500">
            Slug will be generated automatically from the name.
          </p>
        </div>
      </section>

      {/* Markups */}
      <MarkupsEditor
        value={markups}
        onChange={setMarkups}
        disabled={isSubmitting}
        productName={name}
      />

      {/* Tax rules */}
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Tax rules
          </h3>
          <button
            type="button"
            disabled={isSubmitting}
            onClick={handleAddTaxRule}
            className="rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            + Add tax rule
          </button>
        </div>

        {taxRules.length === 0 && (
          <p className="text-xs text-gray-400">
            No tax rules added. You can add GST, service fee, etc.
          </p>
        )}

        <div className="space-y-3">
          {taxRules.map((t, index) => (
            <div
              key={index}
              className="rounded-xl border border-gray-200 bg-gray-50/60 p-3"
            >
              <div className="mb-3 flex items-start justify-between gap-2">
                <div className="text-xs font-semibold text-gray-700">
                  Rule #{index + 1}
                </div>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => handleRemoveTaxRule(index)}
                  className="text-xs text-red-500 hover:text-red-600 disabled:opacity-60"
                >
                  Remove
                </button>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="sm:col-span-1">
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    Tax name
                  </label>
                  <input
                    value={t.taxName}
                    onChange={(e) =>
                      handleUpdateTaxRule(index, {
                        taxName: e.target.value,
                      })
                    }
                    placeholder="e.g. GST"
                    className="w-full rounded-lg border border-gray-300 px-2.5 py-2 text-xs outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30"
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    Rate
                  </label>
                  <input
                    value={t.taxRate}
                    onChange={(e) =>
                      handleUpdateTaxRule(index, {
                        taxRate: e.target.value,
                      })
                    }
                    placeholder="e.g. 5 or 499"
                    className="w-full rounded-lg border border-gray-300 px-2.5 py-2 text-xs outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30"
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    Method
                  </label>
                  <select
                    value={t.taxMethod}
                    onChange={(e) =>
                      handleUpdateTaxRule(index, {
                        taxMethod: e.target.value as TaxMethod,
                      })
                    }
                    className="w-full rounded-lg border border-gray-300 px-2.5 py-2 text-xs outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30"
                  >
                    <option value="PERCENT">% (Percent)</option>
                    <option value="FLAT">Flat (Amount)</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Emails */}
      <section className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Product emails
          </h3>
          <button
            type="button"
            disabled={isSubmitting}
            onClick={handleAddEmail}
            className="rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            + Add email
          </button>
        </div>

        {emails.length === 0 && (
          <p className="text-xs text-gray-400">
            Add product-specific emails like support and operations.
          </p>
        )}

        <div className="space-y-3">
          {emails.map((e, index) => (
            <div
              key={index}
              className="rounded-xl border border-gray-200 bg-gray-50/60 p-3"
            >
              <div className="mb-3 flex items-start justify-between gap-2">
                <div className="text-xs font-semibold text-gray-700">
                  Email #{index + 1}
                </div>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => handleRemoveEmail(index)}
                  className="text-xs text-red-500 hover:text-red-600 disabled:opacity-60"
                >
                  Remove
                </button>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    value={e.email}
                    onChange={(ev) =>
                      handleUpdateEmail(index, {
                        email: ev.target.value,
                      })
                    }
                    placeholder="support@tranzy.com"
                    className="w-full rounded-lg border border-gray-300 px-2.5 py-2 text-xs outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30"
                  />
                </div>
                <div className="sm:col-span-1">
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    Label
                  </label>
                  <input
                    value={e.label}
                    onChange={(ev) =>
                      handleUpdateEmail(index, {
                        label: ev.target.value,
                      })
                    }
                    placeholder="Support, Operations, etc."
                    className="w-full rounded-lg border border-gray-300 px-2.5 py-2 text-xs outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
        >
          {isSubmitting ? "Saving…" : "Save product"}
        </button>
      </div>
    </form>
  );
};
